import { InsuranceBenefitCategory } from './InsurancePlan'
import { Price } from './Price'

export type ProductPrice = {
  netPrice: Price
  unitPrice: Price
  netFullPrice: Price
  unitFullPrice: Price
}

export const enum ProductType {
  GLASSES = 'GLASSES',
  SUNGLASSES = 'SUNGLASSES',
  PRESCRIPTION_SUNGLASSES = 'PRESCRIPTION_SUNGLASSES',

  ZERO_PRICE_BASE_LENS = 'ZERO_PRICE_BASE_LENS',
  PRESCRIPTION_LENS = 'PRESCRIPTION_LENS',
  NO_PRESCRIPTION_LENS = 'NO_PRESCRIPTION_LENS',
  PLANO_LENS = 'PLANO_LENS',
  SINGLE_VISION_LENS = 'SINGLE_VISION_LENS',
  BIFOCAL_LENS = 'BIFOCAL_LENS',
  PROGRESSIVE_LENS = 'PROGRESSIVE_LENS',
  READER_LENS = 'READER_LENS',
  LENS_PACKAGE = 'LENS_PACKAGE',

  PRESCRIPTION_CONTACT_LENS = 'PRESCRIPTION_CONTACT_LENS',
  COLORED_PLANO_CONTACT_LENS = 'COLORED_PLANO_CONTACT_LENS',
  MEDICAL_CONDITION_CONTACT_LENS = 'MEDICAL_CONDITION_CONTACT_LENS',

  FRAME = 'FRAME',
  FRAME_ONLY = 'FRAME_ONLY',
  RIMLESS_FRAME = 'RIMLESS_FRAME',

  PHOTOCHROMATIC_TINT = 'PHOTOCHROMATIC_TINT',
  TINTING = 'TINTING',
  ANTI_REFLECTIVE_TREATMENT = 'ANTI_REFLECTIVE_TREATMENT',
  SCRATCH_RESISTANT_TREATMENT = 'SCRATCH_RESISTANT_TREATMENT',

  LOW_VISION_AID = 'LOW_VISION_AID',
  ELECTRONICS = 'ELECTRONICS',
  ACCESSORIES = 'ACCESSORIES',
  SOLUTIONS = 'SOLUTIONS',
  VR_INSERT = 'VR_INSERT',
  OTHER = 'OTHER',
}

export type ProductTypesEligibility = {
  productTypesEligibility: {
    productType: ProductType
    benefitAvailability: 'SUPPORTED' | 'NOT_SUPPORTED'
  }[]
}

export interface VariantsAvailable {
  baseCurve: string[]
  diameter: string[]
}

export type ProductInsuranceType =
  | 'FUNDED_BENEFIT'
  | 'ADDITIONAL_PAIR_BENEFIT' // has same meaning as ADDITIONAL_PURCHASE_BENEFIT
  | 'ADDITIONAL_PURCHASE_BENEFIT'
  | 'ELIGIBLE_BUT_NOT_COVERED'
  | 'NON_ELIGIBLE'

export type ProductBundle = {
  id: string
  sku: string
  name: string
  brand: string
  prescription: ProductPrescription
  imageURL: string
  quantity: number
  cartProducts: Product[]
  variantsAvailable?: VariantsAvailable
  totalFullPrice: Price
  totalPrice: Price
  bundleId?: string
  claimable: boolean
  includedInPrescription: boolean
  benefitCategory?: InsuranceBenefitCategory
  insuranceType?: ProductInsuranceType
}

export type Product = {
  name: string
  sku: string
  claimProductTypes?: ProductType[]
  quantity: number
  price: ProductPrice
  ecommerceId: string

  // -- CL only attributes
  bodySite?: BodySite
  packSize?: string
  conscat?: string
  // -- end CL only attributes

  /**
   * Applicable for check-eligibility api only
   */
  insuranceEligible?: boolean
}

export const enum BodySite {
  LEFT_EYE = 'LEFT_EYE',
  RIGHT_EYE = 'RIGHT_EYE',
}

export type ProductPrescription = {
  left?: ProductPrescriptionEye
  right?: ProductPrescriptionEye
}

export type ProductPrescriptionEye = {
  options: ProductPrescriptionOptions
}

export const enum ContactLensFamily {
  SOFT = 'SOFT',
  RIGID = 'RIGID',
}

export const enum LensCorrectionType {
  NEAR = 'NEAR',
  FAR = 'FAR',
  MULTIFOCAL = 'MULTIFOCAL',
  PLANO = 'PLANO',
}

export const enum LensType {
  SPHERIC = 'SPHERIC',
  TORIC = 'TORIC',
  MULTIFOCAL_PROGRESSIVE = 'MULTIFOCAL_PROGRESSIVE',
}

export const enum ContactLensRenewalType {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export type ExtraPrescriptionAttributes = {
  brand?: string
  style?: string
  manufacturer?: string
  contactLensFamily?: ContactLensFamily
  lensType?: LensType
  renewal?: ContactLensRenewalType
}

export type ProductPrescriptionOptions = ExtraPrescriptionAttributes & {
  baseCurve?: string
  power?: string
  diameter?: string
  cylinder?: string
  axis?: string
  add?: string
  sphere?: string
  dominance?: string
  monoPupillaryDistance?: string
  pupillaryDistance?: string
  correctionType?: LensCorrectionType
  coloredContactLens?: boolean
  colour?: string
}

export const enum EligibleProductStatus {
  PENDING = 'PENDING',
  ELIGIBLE = 'ELIGIBLE',
  NOT_ELIGIBLE = 'NOT_ELIGIBLE',
  NA = 'NA',
}

export type CartBundle = {
  cartId: string
  orderSubtotal: Price
  shippingCosts: Price
  products: ProductBundle[]
  channel?: string
}
